// @ts-nocheck
/**
 * HostDateTimeHelpers.ts — Pure ISO 8601 datetime helpers for script callHost (no Date in sandbox).
 *
 * Do not import app i18n / settings here — this module is loaded by the template editor (Vite)
 * and must stay free of expo-localization and other RN-only deps.
 */

import { parseDateTimeMs } from '../datetime/parseDateTimeMs';
import type { HostResult } from './IHostBridge';
import {
  formatAppDateTime,
  resolveFormatOptions,
  type AppDateTimeFormatOptions,
} from '../../../../../shims/appStub';

export { parseDateTimeMs };

/** Optional app-supplied resolver for `formatIso` style `local` (language + user prefs). */
let appDateTimeFormatResolver: (() => AppDateTimeFormatOptions | null) | null = null;

/**
 * Register (or clear) the app date/time format resolver used by `hostFormatIso({ style: 'local' })`.
 * Call from app boot; keep null in editor / unit tests.
 */
export function setHostDateTimeFormatResolver(
  resolver: (() => AppDateTimeFormatOptions | null) | null,
): void {
  appDateTimeFormatResolver = resolver;
}

function resolveHostLocalFormatOptions(
  args: Record<string, unknown>,
): AppDateTimeFormatOptions | null {
  const fromArgs = resolveFormatOptions({
    locale: typeof args['locale'] === 'string' ? args['locale'] : undefined,
    dateLocale: typeof args['dateLocale'] === 'string' ? args['dateLocale'] : undefined,
    hour12: typeof args['hour12'] === 'boolean' ? args['hour12'] : undefined,
  });
  if (fromArgs) {
    return fromArgs;
  }

  try {
    return appDateTimeFormatResolver?.() ?? null;
  } catch {
    return null;
  }
}

export function hostAddDurationToIso(args: Record<string, unknown>): HostResult<string> {
  const ms = parseDateTimeMs(args['iso']);
  if (ms === null) {
    return { ok: false, error: { code: 'invalid_request', message: 'Invalid datetime (epoch ms or ISO).' } };
  }
  const days = Number(args['days'] ?? 0) || 0;
  const hours = Number(args['hours'] ?? 0) || 0;
  const minutes = Number(args['minutes'] ?? 0) || 0;
  const delta =
    days * 86_400_000 + hours * 3_600_000 + minutes * 60_000;
  return { ok: true, value: new Date(ms + delta).toISOString() };
}

export function hostDiffIsoDays(args: Record<string, unknown>): HostResult<number> {
  const aMs = parseDateTimeMs(args['a']);
  const bMs = parseDateTimeMs(args['b']);
  if (aMs === null || bMs === null) {
    return { ok: false, error: { code: 'invalid_request', message: 'Invalid datetime (epoch ms or ISO).' } };
  }
  const diffMs = aMs - bMs;
  return { ok: true, value: Math.floor(diffMs / 86_400_000) };
}

export type IsoCompareOp = 'before' | 'after' | 'equal';

export function hostCompareIso(args: Record<string, unknown>): HostResult<boolean> {
  const aMs = parseDateTimeMs(args['a']);
  const bMs = parseDateTimeMs(args['b']);
  const op = String(args['op'] ?? 'before') as IsoCompareOp;
  if (aMs === null || bMs === null) {
    return { ok: false, error: { code: 'invalid_request', message: 'Invalid datetime (epoch ms or ISO).' } };
  }
  if (op === 'equal') return { ok: true, value: aMs === bMs };
  if (op === 'after') return { ok: true, value: aMs > bMs };
  return { ok: true, value: aMs < bMs };
}

export function hostFormatIso(args: Record<string, unknown>): HostResult<string> {
  const ms = parseDateTimeMs(args['iso']);
  if (ms === null) {
    return { ok: false, error: { code: 'invalid_request', message: 'Invalid datetime (epoch ms or ISO).' } };
  }
  const style = String(args['style'] ?? args['format'] ?? 'iso');
  const d = new Date(ms);
  if (style === 'date') {
    // Local calendar day (not UTC) — matches table timedate / chart axis display.
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return { ok: true, value: `${y}-${m}-${day}` };
  }
  if (style === 'local') {
    try {
      const options = resolveHostLocalFormatOptions(args);
      const resolved = options ? resolveFormatOptions(options) : null;
      if (resolved) {
        return { ok: true, value: formatAppDateTime(d, resolved) };
      }
      return { ok: true, value: d.toLocaleString() };
    } catch {
      return { ok: true, value: d.toISOString() };
    }
  }
  return { ok: true, value: d.toISOString() };
}

export type DateTimePart = 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second';
export type DateTimeTimezone = 'local' | 'utc';

const DATE_TIME_PARTS: readonly DateTimePart[] = [
  'year',
  'month',
  'day',
  'hour',
  'minute',
  'second',
];

function parseTimezone(input: unknown): DateTimeTimezone {
  return String(input ?? 'local') === 'utc' ? 'utc' : 'local';
}

function isDateTimePart(value: string): value is DateTimePart {
  return (DATE_TIME_PARTS as readonly string[]).includes(value);
}

function readPart(d: Date, part: DateTimePart, timezone: DateTimeTimezone): number {
  if (timezone === 'utc') {
    switch (part) {
      case 'year':
        return d.getUTCFullYear();
      case 'month':
        return d.getUTCMonth() + 1;
      case 'day':
        return d.getUTCDate();
      case 'hour':
        return d.getUTCHours();
      case 'minute':
        return d.getUTCMinutes();
      case 'second':
        return d.getUTCSeconds();
    }
  }
  switch (part) {
    case 'year':
      return d.getFullYear();
    case 'month':
      return d.getMonth() + 1;
    case 'day':
      return d.getDate();
    case 'hour':
      return d.getHours();
    case 'minute':
      return d.getMinutes();
    case 'second':
      return d.getSeconds();
  }
}

function parseRequiredInt(input: unknown): number | null {
  if (input === undefined || input === null || input === '') return null;
  const n = Number(input);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
  return n;
}

function parseOptionalTimeInt(input: unknown): number | null {
  if (input === undefined || input === null || input === '') return 0;
  const n = Number(input);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
  return n;
}

export function hostGetDateTimePart(args: Record<string, unknown>): HostResult<number> {
  const ms = parseDateTimeMs(args['iso']);
  if (ms === null) {
    return { ok: false, error: { code: 'invalid_request', message: 'Invalid datetime (epoch ms or ISO).' } };
  }
  const partRaw = String(args['part'] ?? '');
  if (!isDateTimePart(partRaw)) {
    return {
      ok: false,
      error: {
        code: 'invalid_request',
        message: 'Invalid part (expected year|month|day|hour|minute|second).',
      },
    };
  }
  const timezone = parseTimezone(args['timezone']);
  return { ok: true, value: readPart(new Date(ms), partRaw, timezone) };
}

export function hostCreateDateTimeFromParts(args: Record<string, unknown>): HostResult<string> {
  const year = parseRequiredInt(args['year']);
  const month = parseRequiredInt(args['month']);
  const day = parseRequiredInt(args['day']);
  const hour = parseOptionalTimeInt(args['hour']);
  const minute = parseOptionalTimeInt(args['minute']);
  const second = parseOptionalTimeInt(args['second']);
  if (year === null || month === null || day === null || hour === null || minute === null || second === null) {
    return {
      ok: false,
      error: {
        code: 'invalid_request',
        message: 'Invalid datetime parts (year, month, day required; hour/minute/second optional integers).',
      },
    };
  }
  if (month < 1 || month > 12) {
    return { ok: false, error: { code: 'invalid_request', message: 'Month must be 1–12.' } };
  }
  if (day < 1 || day > 31) {
    return { ok: false, error: { code: 'invalid_request', message: 'Day must be 1–31.' } };
  }
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59 || second < 0 || second > 59) {
    return {
      ok: false,
      error: { code: 'invalid_request', message: 'Hour/minute/second out of range.' },
    };
  }
  const timezone = parseTimezone(args['timezone']);
  const date =
    timezone === 'utc'
      ? new Date(Date.UTC(year, month - 1, day, hour, minute, second))
      : new Date(year, month - 1, day, hour, minute, second);
  if (Number.isNaN(date.getTime())) {
    return { ok: false, error: { code: 'invalid_request', message: 'Invalid calendar date.' } };
  }
  return { ok: true, value: date.toISOString() };
}

export function hostSetDateTimePart(args: Record<string, unknown>): HostResult<string> {
  const ms = parseDateTimeMs(args['iso']);
  if (ms === null) {
    return { ok: false, error: { code: 'invalid_request', message: 'Invalid datetime (epoch ms or ISO).' } };
  }
  const partRaw = String(args['part'] ?? '');
  if (!isDateTimePart(partRaw)) {
    return {
      ok: false,
      error: {
        code: 'invalid_request',
        message: 'Invalid part (expected year|month|day|hour|minute|second).',
      },
    };
  }
  const value = parseRequiredInt(args['value']);
  if (value === null) {
    return { ok: false, error: { code: 'invalid_request', message: 'Invalid part value (integer required).' } };
  }
  const timezone = parseTimezone(args['timezone']);
  const d = new Date(ms);
  const parts: Record<DateTimePart, number> = {
    year: readPart(d, 'year', timezone),
    month: readPart(d, 'month', timezone),
    day: readPart(d, 'day', timezone),
    hour: readPart(d, 'hour', timezone),
    minute: readPart(d, 'minute', timezone),
    second: readPart(d, 'second', timezone),
  };
  parts[partRaw] = value;
  return hostCreateDateTimeFromParts({ ...parts, timezone });
}

/** Eng short weekday names; index 0 = Sunday … 6 = Saturday. */
export const WEEKDAY_SHORT_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

function readWeekdayIndex(d: Date, timezone: DateTimeTimezone): number {
  return timezone === 'utc' ? d.getUTCDay() : d.getDay();
}

function parseWeekdayIndex(input: unknown): number | null {
  const n = parseRequiredInt(input);
  if (n === null || n < 0 || n > 6) return null;
  return n;
}

/**
 * Inclusive day offset toward `targetWeekday` from `currentWeekday`.
 * Direction +1 = next on-or-after, -1 = last on-or-before.
 * Returns 0 when current already matches (passthrough).
 */
function inclusiveWeekdayDelta(
  currentWeekday: number,
  targetWeekday: number,
  direction: 1 | -1,
): number {
  if (direction === 1) {
    return (targetWeekday - currentWeekday + 7) % 7;
  }
  return (currentWeekday - targetWeekday + 7) % 7;
}

function shiftCalendarDaysPreservingTime(
  ms: number,
  dayDelta: number,
  timezone: DateTimeTimezone,
): HostResult<string> {
  const d = new Date(ms);
  // Calendar-day arithmetic (not raw ms) so DST transitions keep local clock time.
  const shifted =
    timezone === 'utc'
      ? new Date(
          Date.UTC(
            d.getUTCFullYear(),
            d.getUTCMonth(),
            d.getUTCDate() + dayDelta,
            d.getUTCHours(),
            d.getUTCMinutes(),
            d.getUTCSeconds(),
          ),
        )
      : new Date(
          d.getFullYear(),
          d.getMonth(),
          d.getDate() + dayDelta,
          d.getHours(),
          d.getMinutes(),
          d.getSeconds(),
        );
  if (Number.isNaN(shifted.getTime())) {
    return { ok: false, error: { code: 'invalid_request', message: 'Invalid calendar date.' } };
  }
  return { ok: true, value: shifted.toISOString() };
}

export function hostGetCurrentWeekdayIndex(args: Record<string, unknown>): HostResult<number> {
  const timezone = parseTimezone(args['timezone']);
  return { ok: true, value: readWeekdayIndex(new Date(), timezone) };
}

export function hostGetCurrentWeekdayName(args: Record<string, unknown>): HostResult<string> {
  const timezone = parseTimezone(args['timezone']);
  const index = readWeekdayIndex(new Date(), timezone);
  return { ok: true, value: WEEKDAY_SHORT_NAMES[index]! };
}

export function hostGetLastWeekdayBefore(args: Record<string, unknown>): HostResult<string> {
  const ms = parseDateTimeMs(args['iso']);
  if (ms === null) {
    return { ok: false, error: { code: 'invalid_request', message: 'Invalid datetime (epoch ms or ISO).' } };
  }
  const weekday = parseWeekdayIndex(args['weekday']);
  if (weekday === null) {
    return {
      ok: false,
      error: { code: 'invalid_request', message: 'Invalid weekday (expected integer 0–6, Sun=0).' },
    };
  }
  const timezone = parseTimezone(args['timezone']);
  const current = readWeekdayIndex(new Date(ms), timezone);
  const delta = -inclusiveWeekdayDelta(current, weekday, -1);
  if (delta === 0) {
    return { ok: true, value: new Date(ms).toISOString() };
  }
  return shiftCalendarDaysPreservingTime(ms, delta, timezone);
}

export function hostGetNextWeekdayAfter(args: Record<string, unknown>): HostResult<string> {
  const ms = parseDateTimeMs(args['iso']);
  if (ms === null) {
    return { ok: false, error: { code: 'invalid_request', message: 'Invalid datetime (epoch ms or ISO).' } };
  }
  const weekday = parseWeekdayIndex(args['weekday']);
  if (weekday === null) {
    return {
      ok: false,
      error: { code: 'invalid_request', message: 'Invalid weekday (expected integer 0–6, Sun=0).' },
    };
  }
  const timezone = parseTimezone(args['timezone']);
  const current = readWeekdayIndex(new Date(ms), timezone);
  const delta = inclusiveWeekdayDelta(current, weekday, 1);
  if (delta === 0) {
    return { ok: true, value: new Date(ms).toISOString() };
  }
  return shiftCalendarDaysPreservingTime(ms, delta, timezone);
}

export const HOST_DATETIME_METHOD_NAMES = [
  'addDurationToIso',
  'diffIsoDays',
  'compareIso',
  'formatIso',
  'getDateTimePart',
  'createDateTimeFromParts',
  'setDateTimePart',
  'getCurrentWeekdayIndex',
  'getCurrentWeekdayName',
  'getLastWeekdayBefore',
  'getNextWeekdayAfter',
] as const;
